import {process, State} from "@progress/kendo-data-query"
import {Button} from "@progress/kendo-react-buttons"
import {ExcelExport, ExcelExportColumn} from "@progress/kendo-react-excel-export"
import {
    Grid,
    GridCellProps,
    GridColumn,
    GridDataStateChangeEvent,
    GridFooterCellProps,
    GridToolbar,
} from "@progress/kendo-react-grid"
import {useInternationalization} from "@progress/kendo-react-intl"
import {Tooltip} from "@progress/kendo-react-tooltip"
import {useEffect, useRef, useState} from "react"
import {RootState} from "../../../../rootReducer"
import {SpinnerGrid} from "../../../../shared/CommonItems"
import {useAppSelector} from "../../../../store"
import {columnProps} from "../../../../shared/ActiveGridFilter"
import {processExportData} from "../../../../utils/common"

const initialState: State = {skip: 0, take: 50}

const RegionReportGrid = () => {
    const intl = useInternationalization()

    const _export = useRef<ExcelExport | null>(null)
    const [dataState, setDataState] = useState(initialState)
    const regionReport = useAppSelector((state: RootState) => state.containers.passRateReport.prRegionReport)
    const regionReportLoader = useAppSelector(
        (state: RootState) => state.containers.passRateReport.prRegionReportLoader,
    )

    useEffect(() => {
        setDataState(initialState)
    }, [regionReport])

    const CustomColumnCell = (props: GridCellProps) => (
        <td title={props.dataItem[`${props.field}`]}>{props.dataItem[`${props.field}`]}</td>
    )
    const GridHeaderCell = (props: any, subtitle: string) => (
        <a>
            <span title={Object.keys(subtitle).length === 0 ? props.title : subtitle}>{props.title}</span>
            {props.children}
        </a>
    )

    const numberFooterCell = (props: GridFooterCellProps) => {
        const field = props.field || ""
        const total = process(regionReport, dataState).data.reduce(
            (acc: any, current: {[x: string]: any}) => acc + current[field],
            0,
        )
        return (
            <td colSpan={props.colSpan} style={props.style}>
                {total}
            </td>
        )
    }

    const percentageFooterCell = (props: GridFooterCellProps) => {
        const totalTests = process(regionReport, dataState).data.reduce(
            (acc: any, current: {[x: string]: any}) => acc + parseInt(current["tests"]),
            0,
        )
        const totalPass = process(regionReport, dataState).data.reduce(
            (acc: any, current: {[x: string]: any}) => acc + parseInt(current["pass"]),
            0,
        )
        return (
            <td colSpan={props.colSpan} style={props.style} className="">
                {totalPass && totalTests > 0
                    ? intl.formatNumber(totalPass / totalTests, {
                        style: "percent",
                        minimumFractionDigits: 2,
                        maximumFractionDigits  : 2,
                      })
                    : "0.00%"}
            </td>
        )
    }

    return (
        <div className="position-relative">
            {regionReportLoader && <SpinnerGrid />}
            <Tooltip openDelay={100} position="bottom" anchorElement="pointer">
                <ExcelExport
                    fileName={`Region-${intl.formatDate(new Date(), "yyyy-MM-ddTHH-mm-ss")}`}
                    data={processExportData(regionReport, dataState)}
                    ref={_export}
                >
                    <ExcelExportColumn field="regionName" title="Region" />
                    <ExcelExportColumn field="tests" title="Tests" />
                    <ExcelExportColumn field="pass" title="PASS" />
                    <ExcelExportColumn field="fail" title="FAIL" />
                    <ExcelExportColumn field="ftpp" title="FTP %" cellOptions={{format: "0.00\\%"}} width={60} />
                    <ExcelExportColumn field="perOfPass" title="% Of NAT MOT" cellOptions={{format: "0.00\\%"}} />
                    <ExcelExportColumn field="perOfFail" title="Fail % of NAT MOT" cellOptions={{format: "0.00\\%"}} />
                </ExcelExport>

                <Grid
                    style={{
                        height: "calc(100vh - 17.1rem)",
                    }}
                    data={process(regionReport, dataState)}
                    {...dataState}
                    sortable
                    pageable
                    resizable
                    onDataStateChange={(e: GridDataStateChangeEvent) => setDataState(e.dataState)}
                >
                    <GridToolbar>
                        <Button
                            fillMode={"flat"}
                            themeColor="primary"
                            className="buttons-container-button"
                            icon="excel"
                            disabled={process(regionReport, dataState).total === 0}
                            onClick={() => _export.current !== null && _export.current.save()}
                        >
                            Export to Excel
                        </Button>
                    </GridToolbar>
                    <GridColumn
                        {...columnProps("regionName", dataState)}
                        field="regionName"
                        title="Region"
                        cell={CustomColumnCell}
                        footerCell={() => <td>Total</td>}
                        width={170}
                    />
                    <GridColumn
                        field="tests"
                        title="Tests"
                        footerCell={numberFooterCell}
                        width={80}
                        headerCell={props =>
                            GridHeaderCell(props, "MOTs reported by DVSA that contribute towards FTP %")
                        }
                    />

                    <GridColumn
                        field="pass"
                        title="PASS"
                        width={100}
                        headerCell={props =>
                            GridHeaderCell(props, "MOTs that didn't fail in the last 3 months from the booking date")
                        }
                        footerCell={numberFooterCell}
                    />

                    <GridColumn
                        field="fail"
                        title="FAIL"
                        width={100}
                        headerCell={props =>
                            GridHeaderCell(props, "MOTs reported by DVSA along with tests failed in the last 3 months")
                        }
                        footerCell={numberFooterCell}
                    />

                    <GridColumn
                        field="ftpp"
                        title="FTP %"
                        cell={(props: any) => (
                            <td className="">
                                {intl.formatNumber(props.dataItem.ftpp / 100, {
                                    style: "percent",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits  : 2,
                                })}
                            </td>
                        )}
                        headerCell={props => GridHeaderCell(props, "Evaluated by comparing PASS from TotalTests")}
                        footerCell={percentageFooterCell}
                        width={100}
                    />
                    <GridColumn
                        field="perOfPass"
                        title="% Of NAT MOT "
                        cell={(props: any) => (
                            <td className="k-column-align-center">
                                {intl.formatNumber(props.dataItem.perOfPass / 100, {
                                    style: "percent",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits  : 2,
                                })}
                            </td>
                        )}
                        headerCell={props =>
                            GridHeaderCell(props, "Portion of contribution compared to overall Pass figures")
                        }
                        width={120}
                    />
                    <GridColumn
                        field="perOfFail"
                        title="Fail % of NAT MOT"
                        cell={(props: any) => (
                            <td className="k-column-align-center">
                                {intl.formatNumber(props.dataItem.perOfFail / 100, {
                                    style: "percent",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits  : 2,
                                })}
                            </td>
                        )}
                        headerCell={props =>
                            GridHeaderCell(props, "Portion of contribution compared to overall Fail figures")
                        }
                        width={138}
                    />
                </Grid>
            </Tooltip>
        </div>
    )
}

export default RegionReportGrid
